import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { appStyles } from "../../../theme";
import {
  emailInitialValues,
  emailSchema,
  IEmailForm,
} from "./OperatorLicensesUtils";
import {
  useGetOperatorById,
  useSendEmail,
} from "../../../hooks/useOperatorLicenses";
import AppDialog from "../../../components/common/AppDialog";
import AppTextField from "../../../components/common/forms/AppTextField";
import LoadingIndicator from "../../../components/common/LoadingIndicator";
import MultiEmailForm from "../../../components/common/MultiEmailForm";
import MultipleUploadButton from "../../../components/common/MultipleUploadButton";
import MultiFileEmail from "../../../components/common/MultiFileEmail";
import { useSnackBar } from "../../../components/ToastProvider";

const OperatorLicenseEmailForm = () => {
  const { showSnackBar } = useSnackBar();
  const [newTo, setNewTo] = useState<string>("");
  const [newCc, setNewCc] = useState<string>("");
  const [emailAttachmentError, setEmailAttachmentError] = useState<string>("");
  const [hasAttachmentError, setHasAttachmentError] = useState<boolean>(false);
  const navigate = useNavigate();
  const sendEmail = useSendEmail();
  const license = useGetOperatorById();
  const operatorLicenseData = license.data;

  const handleClose = () => navigate("/operator-licenses", { replace: true });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IEmailForm>({
    defaultValues: emailInitialValues,
    mode: "onChange",
    resolver: yupResolver(emailSchema),
  });

  useEffect(() => {
    if (!operatorLicenseData) return;

    const { operatorName } = operatorLicenseData;

    setValue("subject", operatorName);
  }, [operatorLicenseData, setValue]);

  const onSubmit = (email: IEmailForm) => {
    if (newTo || newCc) {
      if (newTo != " " && newCc != " ") {
        showSnackBar(
          `Please ensure all recipients are added or removed before sending the email.`,
          "warning"
        );
        return;
      }
    }

    sendEmail.mutate(email);
    navigate("/operator-licenses", { replace: true });
  };

  const emailFiles = watch("media");

  const { append, remove } = useFieldArray({ control, name: "media" });

  const handleMediaUpload = (files: FileList | null) => {
    if (!files) return;
    setHasAttachmentError(false);

    // check if the resulting number of files is more than 3
    const resultingCount = emailFiles.length + files.length;
    if (resultingCount > 3) {
      setEmailAttachmentError("You can only upload a maximum of 3 files");
      setHasAttachmentError(true);
      return console.log("You can only upload a maximum of 3 files");
    }

    // check for possible duplicate files (lookup value is filename)
    const emailFilenames = emailFiles.map((item) => item.fileName);

    for (let i = 0; i < files.length; i++) {
      if (emailFilenames.includes(files[i].name)) {
        setEmailAttachmentError(`${files[i].name} already exists`);
        setHasAttachmentError(true);
        return console.log(`${files[i].name} already exists`);
      }
    }

    Array.from(files).forEach((file) => {
      append({
        fileName: file.name,
        source: file,
      });
    });
  };

  if (sendEmail.isLoading) return <LoadingIndicator show />;

  return (
    <AppDialog open onDialogClose={handleClose} title="Email PIC" maxWidth="lg">
      <Stack
        component="form"
        height="100%"
        bgcolor="white"
        py={3}
        boxShadow={appStyles.shadow}
        borderRadius={1}
      >
        <Stack px={3} height="100%" overflow="auto" gap={2}>
          <Controller
            control={control}
            name="toemail"
            render={({ field: { onChange, value } }) => (
              <MultiEmailForm
                value={value}
                setValue={onChange}
                newEmailAddress={newTo}
                setNewEmailAddress={setNewTo}
                id="toemail"
                label="To:"
                placeholder="Type here"
                isEmail={true}
                helperText={errors.toemail && "Email is required"}
              />
            )}
          />

          <Controller
            control={control}
            name="ccemail"
            render={({ field: { onChange, value } }) => (
              <MultiEmailForm
                value={value}
                setValue={onChange}
                newEmailAddress={newCc}
                setNewEmailAddress={setNewCc}
                id="ccemail"
                label="Cc:"
                placeholder="Type here"
                isEmail={false}
              />
            )}
          />

          <AppTextField
            label="Subject"
            required={false}
            isMultiline={true}
            id="subject"
            sx={{
              width: "100%",
              alignItems: "start",
              mt: 0.75,
            }}
            helperText={
              errors.subject && (
                <span style={{ color: "#DD4C39", marginLeft: "-14px" }}>
                  Subject is required
                </span>
              )
            }
            {...register("subject")}
          />
          <AppTextField
            label="Message"
            required={false}
            isMultiline={true}
            id="message"
            multiline
            sx={{
              height: 300,
              width: "100%",
              alignItems: "start",
              mt: 0.75,
            }}
            helperText={
              errors.message && (
                <span style={{ color: "#DD4C39", marginLeft: "-14px" }}>
                  Message is required
                </span>
              )
            }
            {...register("message")}
          />
          <Stack direction="column" gap={3} ml="auto">
            <Stack direction="column" gap={2}>
              <Stack
                direction="row"
                gap={3}
                ml="auto"
                justifyContent="space-evenly"
                alignItems="flex-end"
              >
                {emailFiles.map(({ source, fileName }, index) => (
                  <MultiFileEmail
                    key={fileName}
                    label={fileName}
                    onDelete={() => remove(index)}
                  />
                ))}
                <MultipleUploadButton
                  key={emailFiles.length}
                  onChange={(e) => handleMediaUpload(e.currentTarget.files)}
                />
              </Stack>
              <Stack direction="row" justifyContent="end">
                {hasAttachmentError && (
                  <Typography fontSize={12} color="error.main">
                    {emailAttachmentError}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={errors.toemail ? true : false}
              sx={{ width: 150, ml: "auto" }}
            >
              Send
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </AppDialog>
  );
};

export default OperatorLicenseEmailForm;
